import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Clock, MapPin, Plane, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Events() {
  const [events, setEvents] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, promosRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/promotions`)
        ]);
        setEvents(eventsRes.data);
        setPromotions(promosRes.data);
      } catch (err) {
        logger.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="events-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1763651963595-ce388d5dbba6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwbGF1Z2hpbmclMjBwYXJ0eSUyMG5pZ2h0fGVufDB8fHx8MTc3MzE5MTE2Mnww&ixlib=rb-4.1.0&q=85')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-casino-dark" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            What's Happening
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Events & <span className="gold-gradient">Promotions</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Stay updated on special events, promotions, and exciting opportunities at San Marcos Social Club.
          </p>
        </div>
      </section>

      {/* Featured Promotion */}
      {promotions.length > 0 && (
        <section className="py-20 bg-casino-black" data-testid="featured-promotion">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-gold uppercase tracking-widest text-sm mb-4">Don't Miss Out</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white">Featured Promotion</h2>
            </div>

            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-casino-red/30 to-gold/20 border border-gold/30">
              <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-casino-red/10 rounded-full blur-3xl" />
              
              <div className="relative p-8 md:p-12 lg:p-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Plane className="w-6 h-6 text-gold" />
                      <span className="text-gold uppercase tracking-widest text-sm font-accent">
                        Grand Prize
                      </span>
                    </div>
                    <h3 className="font-heading text-4xl md:text-5xl text-white mb-6">
                      {promotions[0].title}
                    </h3>
                    <p className="text-white/70 text-lg mb-8">
                      {promotions[0].description}
                    </p>
                    <div className="flex flex-wrap gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gold" />
                        <span className="text-white/60">Jan 1 - May 31</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-gold" />
                        <span className="text-white/60">Drawing June 6th</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="w-64 h-64 rounded-full bg-gradient-to-br from-gold/30 to-casino-red/20 flex items-center justify-center">
                        <div className="w-48 h-48 rounded-full bg-black/50 flex items-center justify-center text-center p-4">
                          <div>
                            <Plane className="w-12 h-12 text-gold mx-auto mb-2" />
                            <p className="text-gold font-heading text-xl">Las Vegas</p>
                            <p className="text-white/60 text-sm">Trip Giveaway</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Live Cash Games */}
      <section className="py-20 bg-casino-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="glass-card p-8 md:p-12 text-center">
            <Star className="w-16 h-16 text-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Live Cash Games Daily
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
              Join us at San Marcos Social Club for live cash games daily in our vibrant poker room. 
              Whether you're learning how to play poker or you're an experienced pro, we have a seat for you.
            </p>
            <div className="flex flex-wrap justify-center gap-8">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-10 h-10 text-gold" />
                </div>
                <p className="text-white font-medium">Open Daily</p>
                <p className="text-white/40 text-sm italic">Coming Soon</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-10 h-10 text-gold" />
                </div>
                <p className="text-white font-medium">Location</p>
                <p className="text-white/40 text-sm italic">Coming Soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Events Grid */}
      <section className="py-20 bg-casino-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-gold uppercase tracking-widest text-sm mb-4">Every Week</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">Weekly Events</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 6).map((event, index) => (
              <div 
                key={event.id}
                className="glass-card p-6"
                data-testid={`event-card-${index}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-gold" />
                  <span className="text-gold uppercase tracking-wider text-sm">{event.day}</span>
                </div>
                <h3 className="font-heading text-xl text-white mb-2">{event.event_name}</h3>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{event.start_time}</span>
                </div>
                {event.guarantee && (
                  <p className="text-gold text-sm mt-2 font-medium">{event.guarantee}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
